import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, Phone, MapPin, Building, Loader2, X, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export default function ProfileModal({ isOpen, onClose, userPhone, onProfileUpdate }) {
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [errors, setErrors] = useState({});
  
  const [profile, setProfile] = useState({
    phone: userPhone || '',
    name: '',
    address: '',
    city: '',
    email: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isOpen) return;
    
    // Fetch Cities
    axios.get(`${API_URL}/api/cities`).then(res => {
      setCities(res.data);
    }).catch(err => console.error("Error fetching cities"));

    // Fetch Profile
    if (userPhone) {
      axios.get(`${API_URL}/api/profile/${userPhone}`).then(res => {
        if (res.data) {
          setProfile({
            ...res.data,
            name: (res.data.name && res.data.name !== 'મહેમાન (Guest)') ? res.data.name : ''
          });
        }
      }).catch(err => console.error("Error fetching profile"));
    }
  }, [isOpen, userPhone, API_URL]);

  const validateForm = () => {
    const newErrors = {};
    if (!profile.name || profile.name.trim() === '' || profile.name === 'મહેમાન (Guest)') newErrors.name = 'આ માહિતી જરૂરી છે';
    if (!profile.phone || profile.phone.trim() === '') newErrors.phone = 'આ માહિતી જરૂરી છે';
    if (!profile.city || profile.city.trim() === '') newErrors.city = 'આ માહિતી જરૂરી છે';
    if (!profile.address || profile.address.trim() === '') newErrors.address = 'આ માહિતી જરૂરી છે';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const userToken = localStorage.getItem('userToken');
      if (!userToken) {
        toast.error('પ્રોફાઇલ સેવ કરવા માટે લૉગિન કરો (Please login to save profile)');
        setLoading(false);
        return;
      }
      const res = await axios.post(
        `${API_URL}/api/profile`,
        { ...profile, phone: userPhone || profile.phone },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      toast.success('પ્રોફાઇલ અપડેટ સફળતાપૂર્વક થઈ ગઈ છે');
      if (onProfileUpdate) onProfileUpdate(res.data);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'પ્રોફાઇલ અપડેટ કરવામાં નિષ્ફળ.';
      toast.error(typeof msg === 'string' ? msg : 'પ્રોફાઇલ અપડેટ કરવામાં નિષ્ફળ.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-[#172554]/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl w-full max-w-sm p-6 relative z-10 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-black transition-colors bg-gray-50 rounded-full p-1">
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-3 overflow-hidden border border-blue-200 shadow-inner">
                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.name}`} alt="Avatar" className="w-full h-full" />
              </div>
              <h2 className="text-xl font-bold text-[#172554] text-gujarati mb-1">પ્રોફાઇલ (Profile)</h2>
              <p className="text-[#64748B] text-xs text-gujarati">તમારી વિગતો અપડેટ કરો</p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-3">
              <div>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#172554]/50 w-4 h-4" />
                  <input 
                    type="text" required
                    className={`w-full pl-9 pr-3 py-2 text-sm border ${errors.name ? 'border-red-500' : 'border-[#E2E8F0]'} bg-gray-50 rounded-lg focus:ring-1 focus:ring-[#172554] focus:bg-white outline-none font-normal`}
                    value={profile.name} onChange={e => { setProfile({...profile, name: e.target.value}); if (errors.name) setErrors({...errors, name: null}); }}
                    placeholder="તમારું નામ દાખલ કરો..."
                    data-testid="profile-name-input"
                  />
                </div>
                {errors.name && <p className="text-red-500 text-[11px] mt-1 font-medium text-gujarati" data-testid="profile-error-name">{errors.name}</p>}
              </div>
              
              <div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#172554]/50 w-4 h-4" />
                  <input 
                    type="tel" required
                    disabled={!!userPhone} // disabled if logged in
                    className={`w-full pl-9 pr-3 py-2 text-sm border ${errors.phone ? 'border-red-500' : 'border-[#E2E8F0]'} rounded-lg outline-none font-normal ${userPhone ? 'bg-gray-100 text-[#64748B]' : 'bg-gray-50 focus:ring-1 focus:ring-[#172554] focus:bg-white'}`}
                    value={profile.phone} onChange={e => { setProfile({...profile, phone: e.target.value}); if (errors.phone) setErrors({...errors, phone: null}); }}
                    placeholder="મોબાઈલ નંબર (Mobile)"
                    data-testid="profile-phone-input"
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-[11px] mt-1 font-medium text-gujarati" data-testid="profile-error-phone">{errors.phone}</p>}
              </div>

              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#172554]/50 w-4 h-4" />
                  <input
                    type="email"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-[#E2E8F0] bg-gray-50 rounded-lg focus:ring-1 focus:ring-[#172554] focus:bg-white outline-none font-normal"
                    value={profile.email || ''} onChange={e => setProfile({...profile, email: e.target.value})}
                    placeholder="ઈમેલ (Email — Optional)"
                    data-testid="profile-email-input"
                  />
                </div>
              </div>

              <div>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-[#172554]/50 w-4 h-4" />
                  <select 
                    required
                    className={`w-full pl-9 pr-3 py-2 text-sm border ${errors.city ? 'border-red-500' : 'border-[#E2E8F0]'} bg-gray-50 rounded-lg focus:ring-1 focus:ring-[#172554] focus:bg-white outline-none font-medium text-gujarati appearance-none`}
                    value={profile.city} onChange={e => { setProfile({...profile, city: e.target.value}); if (errors.city) setErrors({...errors, city: null}); }}
                    data-testid="profile-city-select"
                  >
                    <option value="" disabled>શહેર પસંદ કરો</option>
                    {cities.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                    {cities.length === 0 && <option value="સુરત (Surat)">સુરત (Surat)</option>}
                  </select>
                </div>
                {errors.city && <p className="text-red-500 text-[11px] mt-1 font-medium text-gujarati" data-testid="profile-error-city">{errors.city}</p>}
              </div>

              <div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 text-[#172554]/50 w-4 h-4" />
                  <textarea 
                    rows="2"
                    className={`w-full pl-9 pr-3 py-2 text-sm border ${errors.address ? 'border-red-500' : 'border-[#E2E8F0]'} bg-gray-50 rounded-lg focus:ring-1 focus:ring-[#172554] focus:bg-white outline-none font-normal resize-none`}
                    value={profile.address} onChange={e => { setProfile({...profile, address: e.target.value}); if (errors.address) setErrors({...errors, address: null}); }}
                    placeholder="સરનામું (Address)"
                    data-testid="profile-address-textarea"
                  ></textarea>
                </div>
                {errors.address && <p className="text-red-500 text-[11px] mt-1 font-medium text-gujarati" data-testid="profile-error-address">{errors.address}</p>}
              </div>

              <button 
                type="submit" disabled={loading}
                className="w-full bg-[#F5B800] text-[#172554] py-2.5 rounded-lg font-bold hover:bg-[#FFC928] transition-colors text-gujarati shadow-sm flex justify-center items-center gap-2 mt-4 text-sm"
                data-testid="profile-save-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'સેવ કરો (Save)'}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}